import apiClient from './apiClient';
import { WmsTenant, TenantBaseline, ProvisionMode, CleanupTableConfig, CleanupDatabaseRequest, CleanupPreview, CleanupDatabaseResult, CompanyNoColumnInfo, UpdateCompanyNoResult, TenantCompanyInfo } from '../types/WmsTenant';

export const tenantApi = {
  getAllTenants: async (): Promise<WmsTenant[]> => {
    const response = await apiClient.get<WmsTenant[]>('/api/wmsTenants');
    return response.data;
  },

  getTenantById: async (id: number): Promise<WmsTenant> => {
    const response = await apiClient.get<WmsTenant>(`/api/wmsTenants/${id}`);
    return response.data;
  },

  createTenant: async (tenant: WmsTenant): Promise<WmsTenant> => {
    const response = await apiClient.post<WmsTenant>('/api/wmsTenants', tenant);
    return response.data;
  },

  updateTenant: async (id: number, tenant: WmsTenant): Promise<WmsTenant> => {
    const response = await apiClient.put<WmsTenant>(`/api/wmsTenants/${id}`, tenant);
    return response.data;
  },

  deleteTenant: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/wmsTenants/${id}`);
  },

  rotateDashboardKey: async (tenantId: number): Promise<{message: string}> => {
    const response = await apiClient.post(`/api/wmsTenants/${tenantId}/rotate-dashboard-key`);
    return response.data;
  },

  provisionDatabase: async (tenantId: number, mode: ProvisionMode, templateDatabase?: string): Promise<{message: string, databaseName: string, serverName: string}> => {
    const response = await apiClient.post(`/api/wmsTenants/${tenantId}/provision-database`,
      { mode, templateDatabase: mode === 'clone' ? templateDatabase : undefined }
    );
    return response.data;
  },

  getActiveBaseline: async (): Promise<TenantBaseline | null> => {
    const response = await apiClient.get<{ baseline: TenantBaseline | null }>('/api/wmsTenants/active-baseline');
    return response.data.baseline;
  },

  getDatabaseStatus: async (tenantId: number): Promise<{databaseExists: boolean, databaseName: string | null, serverName: string | null}> => {
    const response = await apiClient.get(`/api/wmsTenants/${tenantId}/database-status`);
    return response.data;
  },

  getTemplateDatabases: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/api/wmsTenants/template-databases');
    return response.data;
  },

  // Database Cleanup APIs
  getCleanupTables: async (): Promise<CleanupTableConfig[]> => {
    const response = await apiClient.get<CleanupTableConfig[]>('/api/wmsTenants/cleanup-tables');
    return response.data;
  },

  createCleanupTable: async (dto: Omit<CleanupTableConfig, 'id' | 'createdDate' | 'modifiedDate'>): Promise<CleanupTableConfig> => {
    const response = await apiClient.post<CleanupTableConfig>('/api/wmsTenants/cleanup-tables', dto);
    return response.data;
  },

  updateCleanupTable: async (id: number, dto: Omit<CleanupTableConfig, 'id' | 'createdDate' | 'modifiedDate'>): Promise<CleanupTableConfig> => {
    const response = await apiClient.put<CleanupTableConfig>(`/api/wmsTenants/cleanup-tables/${id}`, dto);
    return response.data;
  },

  deleteCleanupTable: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/wmsTenants/cleanup-tables/${id}`);
  },

  getCleanupPreview: async (tenantId: number, request?: CleanupDatabaseRequest): Promise<CleanupPreview> => {
    const response = await apiClient.post<CleanupPreview>(
      `/api/wmsTenants/${tenantId}/cleanup-preview`,
      request || {}
    );
    return response.data;
  },

  cleanupDatabase: async (tenantId: number, request: CleanupDatabaseRequest): Promise<CleanupDatabaseResult> => {
    const response = await apiClient.post<CleanupDatabaseResult>(
      `/api/wmsTenants/${tenantId}/cleanup`,
      request
    );
    return response.data;
  },

  // CompanyNo Update APIs
  getCompanyNoColumns: async (tenantId: number): Promise<CompanyNoColumnInfo[]> => {
    const response = await apiClient.get<CompanyNoColumnInfo[]>(
      `/api/wmsTenants/${tenantId}/companyno-columns`
    );
    return response.data;
  },

  getCompanies: async (tenantId: number): Promise<TenantCompanyInfo[]> => {
    const response = await apiClient.get<TenantCompanyInfo[]>(
      `/api/wmsTenants/${tenantId}/companies`
    );
    return response.data;
  },

  updateCompanyNo: async (tenantId: number, newCompanyNo: string, oldCompanyNo?: string): Promise<UpdateCompanyNoResult> => {
    const response = await apiClient.post<UpdateCompanyNoResult>(
      `/api/wmsTenants/${tenantId}/update-companyno`,
      { newCompanyNo, oldCompanyNo: oldCompanyNo || null }
    );
    return response.data;
  },

  // Setup Script
  downloadSetupScript: async (tenantId: number): Promise<void> => {
    const response = await apiClient.get(`/api/wmsTenants/${tenantId}/setup-script`, {
      responseType: 'blob'
    });

    // Hent filnavn fra Content-Disposition header eller bruk default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `Setup_Tenant_${tenantId}.ps1`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Opprett nedlastingslenke
    const blob = new Blob([response.data], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export default apiClient;